import { theme } from 'antd';
import SuperFetch from 'library/helpers/superFetch';
import { removeEmptyOrNull } from 'library/helpers/utility';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { isEqual } from 'lodash';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectDefaultCategories } from 'reduxStore/defaultCategories/selectors';
import { Notification } from 'zComponents/atoms';
import { Button, Form, Input, Modal, Select, Tooltip } from 'zui/Antd';

const AddDefaultCategoryModal = ({ open, setOpen, orgID, children, refetch, onLoadingChange }) => {
	const {
		token: { fontWeightStrong, margin }
	} = theme.useToken();

	const [loading, setLoading] = useState(false);
	const [isFormValid, setIsFormValid] = useState(false);
	const defaultCategories = useSelector(selectDefaultCategories);
	const [form] = Form.useForm();

	const previousValuesRef = useRef({});

	const options = defaultCategories.map((defaultCategory) => ({
		value: defaultCategory.categoryID,
		label: defaultCategory.name
	}));

	const handleFormChange = async () => {
		const currentValues = form.getFieldsValue();
		const hasChanged = !isEqual(currentValues, previousValuesRef.current);
		const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
		const hasRequiredValues = currentValues.categoryName && currentValues.defaultCategoryID;

		setIsFormValid(hasChanged && !hasErrors && hasRequiredValues);
	};

	useEffect(() => {
		if (open) {
			form.resetFields();
			setIsFormValid(false);
			previousValuesRef.current = form.getFieldsValue();
		}
	}, [open, form]);

	const handleCreateDefaultCategory = async () => {
		onLoadingChange?.(true);
		setLoading(true);
		try {
			const formData = removeEmptyOrNull(form.getFieldsValue(true));
			const res = await SuperFetch.post(
				'/deviceCategories/defaultCategories/createManualCategory',
				{
					name: formData.categoryName,
					defaultCategoryID: formData.defaultCategoryID
				},
				{},
				orgID
			);

			if (res.statusCode === 200) {
				await refetch?.();
				Notification('success', 'The default category has been created.');
				handleCancel();
			}
		} catch (error) {
			Notification('error', 'Failed, please try again.');
		} finally {
			setLoading(false);
			onLoadingChange?.(false);
		}
	};

	const handleCancel = () => {
		if (!loading) {
			setOpen(false);
			form.resetFields();
			setIsFormValid(false);
		}
	};

	return (
		<Fragment>
			<Tooltip title='Add template'>
				{children ? (
					children
				) : (
					<Button type='primary' onClick={() => setOpen(true)}>
						Add Template
					</Button>
				)}
			</Tooltip>

			<Modal
				title='Add Template'
				open={open}
				onCancel={handleCancel}
				closable={!loading}
				maskClosable={!loading}
				keyboard={!loading}
				destroyOnClose
				footer={[
					<Button key='cancel' onClick={handleCancel} disabled={loading}>
						Cancel
					</Button>,
					<Button
						key='create'
						type='primary'
						loading={loading}
						onClick={handleCreateDefaultCategory}
						disabled={!isFormValid || loading}
					>
						Add
					</Button>
				]}
			>
				<Form form={form} layout='vertical' onValuesChange={handleFormChange}>
					<Form.Item
						label='Name'
						name='categoryName'
						rules={fieldsValidator.Name}
						style={{ marginTop: margin, fontWeight: fontWeightStrong }}
						required={false}
					>
						<Input placeholder='Enter name for new category' disabled={loading} />
					</Form.Item>
					<Form.Item
						label='Default Category'
						name='defaultCategoryID'
						rules={[{ required: true, message: 'Please select a default category' }]}
						style={{ marginTop: margin, fontWeight: fontWeightStrong }}
						required={false}
					>
						<Select placeholder='Select a category' allowClear options={options} disabled={loading} />
					</Form.Item>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default AddDefaultCategoryModal;
